import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import router from '../router'

// Token refresh state management
let isRefreshingToken = false
let refreshSubscribers: ((token: string) => void)[] = []

// Subscribe to token refresh completion
const subscribeTokenRefresh = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback)
}

// Notify all subscribers when token is refreshed
const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach(callback => callback(token))
  refreshSubscribers = []
}

// Clear auth and redirect to login
const clearAuthAndRedirect = () => {
  localStorage.removeItem('token')
  localStorage.removeItem('token_expires_at')
  router.push('/login')
}

// Dynamically determine API URL based on current environment
const getBaseURL = () => {
  // Priority 1: Explicit VITE_API_URL from environment
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL
  }

  // Priority 2: Production mode - use same origin (nginx proxies /api to backend)
  if (import.meta.env.PROD) {
    return window.location.origin
  }

  // Priority 3: Development without VITE_API_URL - configuration error
  console.warn(
    '[API Config] VITE_API_URL not set in development mode. ' +
    'Create frontend/.env.local with VITE_API_URL=http://localhost:8001'
  )

  // Fallback to same origin to avoid hardcoded ports
  return window.location.origin
}

// Exponential backoff retry configuration
interface RetryConfig {
  retries?: number
  retryDelay?: number
  shouldRetry?: boolean
  currentRetryAttempt?: number
}

const api = axios.create({
  baseURL: getBaseURL(),
  timeout: 30000, // 30s — prevents infinite hangs on poor mobile networks
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  },
  withCredentials: true // Required for cross-origin requests
})

// Helper function for exponential backoff
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

// Calculate exponential backoff delay
const getExponentialBackoffDelay = (attempt: number): number => {
  const baseDelay = 1000 // 1 second
  const maxDelay = 30000 // 30 seconds
  const delay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay)
  // Add jitter to prevent thundering herd
  const jitter = Math.random() * delay * 0.1
  return delay + jitter
}

// Extend axios config type
declare module 'axios' {
  export interface AxiosRequestConfig {
    retryConfig?: RetryConfig
  }
}

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add Authorization header from localStorage token
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Initialize retry config if not set
    if (!config.retryConfig) {
      config.retryConfig = {
        retries: 3,
        shouldRetry: true,
        currentRetryAttempt: 0
      }
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Extend request config to track retry attempts
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
  _retryCount?: number
}

// Response interceptor with exponential backoff retry logic and 401 token refresh
api.interceptors.response.use(
  (response) => {
    // Reset retry attempt on successful response
    if (response.config.retryConfig) {
      response.config.retryConfig.currentRetryAttempt = 0
    }
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as ExtendedAxiosRequestConfig | undefined

    if (!originalRequest) {
      return Promise.reject(error)
    }

    // Handle 401 Unauthorized - attempt token refresh before logout
    if (error.response?.status === 401) {
      // Don't retry if this IS the refresh request or login request
      const isAuthEndpoint = originalRequest.url?.includes('/api/auth/refresh') ||
                             originalRequest.url?.includes('/api/auth/login') ||
                             originalRequest.url?.includes('/api/auth/logout')

      if (isAuthEndpoint) {
        console.log('[Axios] Auth endpoint returned 401, clearing auth')
        clearAuthAndRedirect()
        return Promise.reject(error)
      }

      // Don't retry if we've already retried this request
      if (originalRequest._retry) {
        console.log('[Axios] Already retried this request, clearing auth')
        clearAuthAndRedirect()
        return Promise.reject(error)
      }

      // If already refreshing, wait for refresh to complete
      if (isRefreshingToken) {
        console.log('[Axios] Token refresh in progress, waiting...')
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((newToken: string) => {
            // Update the Authorization header with new token
            originalRequest.headers.Authorization = `Bearer ${newToken}`
            originalRequest._retry = true
            resolve(api(originalRequest))
          })
          // Timeout after 10 seconds
          setTimeout(() => {
            reject(new Error('Token refresh timeout'))
          }, 10000)
        })
      }

      // Start token refresh
      isRefreshingToken = true
      originalRequest._retry = true

      try {
        console.log('[Axios] Attempting token refresh...')
        const response = await api.post('/api/auth/refresh')
        const { token: newToken } = response.data

        if (newToken) {
          // Store new token
          localStorage.setItem('token', newToken)
          const expiresAt = Date.now() + (1440 * 60 * 1000) // 24 hours
          localStorage.setItem('token_expires_at', expiresAt.toString())

          console.log('[Axios] Token refreshed successfully')

          // Notify subscribers
          onTokenRefreshed(newToken)

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${newToken}`
          return api(originalRequest)
        }

        throw new Error('No token in refresh response')
      } catch (refreshError) {
        console.error('[Axios] Token refresh failed:', refreshError)
        clearAuthAndRedirect()
        return Promise.reject(refreshError)
      } finally {
        isRefreshingToken = false
      }
    }

    // Handle 403 Forbidden - don't retry
    if (error.response?.status === 403) {
      return Promise.reject(error)
    }

    // Check if we should retry (for 502, 503, 504, timeout errors and network errors)
    const shouldRetry =
      !error.response || // Network error
      error.code === 'ECONNABORTED' || // Timeout
      error.response.status === 502 || // Bad Gateway
      error.response.status === 503 || // Service Unavailable
      error.response.status === 504 || // Gateway Timeout
      error.response.status === 429    // Too Many Requests

    if (shouldRetry && originalRequest?.retryConfig) {
      const { retries = 3, shouldRetry: allowRetry = true, currentRetryAttempt = 0 } = originalRequest.retryConfig

      // Check if retries are enabled and we haven't exceeded max retries
      if (allowRetry && currentRetryAttempt < retries) {
        originalRequest.retryConfig.currentRetryAttempt = currentRetryAttempt + 1

        // Calculate delay with exponential backoff
        const delay = getExponentialBackoffDelay(currentRetryAttempt)

        // Wait before retrying
        await sleep(delay)

        // Retry the request
        return api(originalRequest)
      }
    }

    // Don't logout on network errors
    return Promise.reject(error)
  }
)

export default api
